import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { toggleOpenDynamicSeriesAction } from 'store/actions/dynamicSeries';
import { getLayersAction, setCurrentLayerAction } from 'store/actions/layers';
import { CloseWrapper, ModalContainer } from './styled';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  IconButton,
  Box,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';

export const DynamicSeriesSlider: React.FC = () => {
  const [visibleYears, setVisibleYears] = useState<number>(0);
  const [currentYear, setCurrentYear] = useState<any>(null);
  const { dynamicYears, isOpen } = useAppSelector(
    (state) => state.dynamicSeries
  );
  const { currentYear: defaultYear } = useAppSelector((state) => state.layers);
  const { isLogged } = useAppSelector((state) => state.mainPage);
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');

  const updateVisibleYears = (direction: 'left' | 'right') => {
    setVisibleYears((prevVisibleYears) => {
      let newVisibleYears = prevVisibleYears;

      if (direction === 'left' && prevVisibleYears > 0) {
        newVisibleYears = prevVisibleYears - 1;
      } else if (
        direction === 'right' &&
        prevVisibleYears < dynamicYears.length - 5
      ) {
        newVisibleYears = prevVisibleYears + 1;
      }

      const startIndex = newVisibleYears;
      const endIndex = newVisibleYears + 5;
      const currentYearIndex = dynamicYears.indexOf(currentYear);

      if (currentYearIndex < startIndex) {
        setCurrentYear(dynamicYears[startIndex]);
      } else if (currentYearIndex >= endIndex) {
        setCurrentYear(dynamicYears[endIndex - 1]);
      }

      return newVisibleYears;
    });
  };

  const handleYearChangeRadio = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const yearIndex = Number(event.target.value);
    setCurrentYear(dynamicYears[yearIndex]);
  };

  const handleYearChangeSelect = (event: SelectChangeEvent<number>) => {
    const yearIndex = Number(event.target.value);
    setCurrentYear(dynamicYears[yearIndex]);
  };

  const closeModal = () => {
    dispatch(toggleOpenDynamicSeriesAction());
  };

  useEffect(() => {
    if (!dynamicYears.length && isOpen) closeModal();
  }, [dynamicYears]);

  useEffect(() => {
    if (!isOpen) return;

    const defaultActiveYear = dynamicYears.find(
      (item) => item.year === +defaultYear
    );

    if (defaultActiveYear) {
      setCurrentYear(defaultActiveYear);
      const defaultYearIndex = dynamicYears.indexOf(defaultActiveYear);

      if (
        defaultYearIndex < visibleYears ||
        defaultYearIndex >= visibleYears + 5
      ) {
        const newVisibleYears = Math.max(
          0,
          Math.min(defaultYearIndex - 2, dynamicYears.length - 5)
        );
        setVisibleYears(newVisibleYears);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!currentYear || currentYear.year === +defaultYear) return;
    dispatch(getLayersAction(isLogged, String(currentYear.year))).then(
      (res) => {
        const newLayer = res.allChildren.find(
          (item: any) =>
            item.dataset === currentYear.dataset && item.id === currentYear.id
        );
        if (newLayer) {
          dispatch(
            setCurrentLayerAction(
              newLayer.layer,
              newLayer.url,
              newLayer.id,
              newLayer.legend_file,
              newLayer.map_file,
              newLayer.meta_file,
              newLayer.dataset,
              newLayer.name,
              newLayer.legend_size,
              newLayer.scale_1_50
            )
          );
        }
      }
    );
  }, [currentYear]);

  const displayedYears = dynamicYears.slice(visibleYears, visibleYears + 5);
  const yearIndex = dynamicYears.indexOf(currentYear);

  return !isOpen ? null : (
    <ModalContainer>
      <CloseWrapper>
        <IconButton
          edge="start"
          color="inherit"
          onClick={closeModal}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </CloseWrapper>
      {isMobile ? (
        <Select
          value={yearIndex}
          onChange={handleYearChangeSelect}
          displayEmpty
          fullWidth
          size="small"
        >
          {dynamicYears.map((year, index) => (
            <MenuItem key={index} value={index}>
              {year.year}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Box
          sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}
        >
          {dynamicYears.length > 5 && (
            <IconButton
              onClick={() => updateVisibleYears('left')}
              disabled={visibleYears === 0}
            >
              <KeyboardArrowLeftIcon />
            </IconButton>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: '63%',
              left: dynamicYears.length > 5 ? '60px' : '30px',
              right: '60px',
              height: '5px',
              backgroundColor: '#ccc',
              zIndex: 1,
            }}
          />
          <RadioGroup
            value={dynamicYears.indexOf(currentYear)}
            onChange={handleYearChangeRadio}
            row
          >
            {displayedYears.map((year, index) => (
              <FormControlLabel
                sx={{
                  ml: 1.5,
                  mr: 1.5,
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 'bold',
                    fontSize:
                      yearIndex === index + visibleYears ? '16px' : '13px',
                  },
                }}
                key={index}
                labelPlacement="top"
                value={visibleYears + index}
                control={
                  <Radio
                    sx={{
                      zIndex: 2,
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#fff',
                        borderRadius: '50%',
                      },
                    }}
                  />
                }
                label={year.year.toString()}
              />
            ))}
          </RadioGroup>
          {dynamicYears.length > 5 ? (
            <IconButton
              onClick={() => updateVisibleYears('right')}
              disabled={visibleYears >= dynamicYears.length - 5}
            >
              <KeyboardArrowRightIcon />
            </IconButton>
          ) : (
            <Box sx={{ mr: 3 }} />
          )}
        </Box>
      )}
    </ModalContainer>
  );
};
